import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Download,
  LogOut,
  Search,
  RefreshCw,
  Mail,
  Phone,
  Calendar,
  X,
  Check,
  Archive,
  Inbox,
  Loader2,
} from "lucide-react";
import { toast } from "sonner";
import {
  adminListApplications,
  adminGetStats,
  adminUpdateStatus,
  adminDownloadCsv,
  clearAdminToken,
  getAdminToken,
} from "@/lib/api";

const STATUS_STYLES = {
  new: "bg-[#eab308] text-black",
  contacted: "bg-[#1a1a1a] text-[#eab308] border border-[#eab308]/40",
  archived: "bg-[#1a1a1a] text-neutral-400 border border-[#2a2a2a]",
};

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [apps, setApps] = useState([]);
  const [stats, setStats] = useState({ total: 0, new: 0, contacted: 0, archived: 0 });
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [filters, setFilters] = useState({
    training_type: "",
    level: "",
    status: "",
    q: "",
  });
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    if (!getAdminToken()) {
      navigate("/admin", { replace: true });
      return;
    }
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const load = async () => {
    try {
      setRefreshing(true);
      const [list, s] = await Promise.all([
        adminListApplications(filters),
        adminGetStats(),
      ]);
      setApps(list);
      setStats(s);
    } catch (e) {
      if (e?.response?.status === 401) {
        clearAdminToken();
        navigate("/admin", { replace: true });
        return;
      }
      toast.error("Failed to load applications");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const onApplyFilters = (e) => {
    e?.preventDefault?.();
    load();
  };

  const logout = () => {
    clearAdminToken();
    navigate("/admin");
  };

  const updateStatus = async (id, status) => {
    try {
      await adminUpdateStatus(id, status);
      setApps((prev) => prev.map((a) => (a.id === id ? { ...a, status } : a)));
      if (selected?.id === id) setSelected({ ...selected, status });
      const s = await adminGetStats();
      setStats(s);
      toast.success(`Marked as ${status}`);
    } catch (e) {
      toast.error("Update failed");
    }
  };

  const exportCsv = async () => {
    try {
      await adminDownloadCsv();
      toast.success("CSV downloaded");
    } catch (e) {
      toast.error("Export failed");
    }
  };

  const filtered = useMemo(() => apps, [apps]);

  return (
    <main
      data-testid="admin-dashboard-page"
      className="min-h-screen bg-[#0a0a0a] text-white"
    >
      {/* Header */}
      <header className="sticky top-0 z-30 bg-black/85 backdrop-blur-md border-b border-[#2a2a2a]">
        <div className="max-w-7xl mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="inline-block w-2 h-2 rounded-full bg-[#eab308] bham-dot" />
            <span className="font-display text-xl md:text-2xl tracking-tight">
              BHAM<span className="text-[#eab308]">.</span>BASEBALL
            </span>
            <span className="hidden md:inline-block ml-3 px-2 py-1 border border-[#2a2a2a] font-condensed uppercase text-[10px] tracking-[0.2em] text-neutral-400">
              Admin
            </span>
          </div>
          <div className="flex items-center gap-3">
            <button
              data-testid="admin-export-btn"
              onClick={exportCsv}
              className="inline-flex items-center gap-2 border border-[#2a2a2a] hover:border-[#eab308] hover:text-[#eab308] px-3 md:px-4 py-2 font-condensed uppercase tracking-[0.2em] text-xs"
            >
              <Download size={14} /> <span className="hidden sm:inline">Export CSV</span>
            </button>
            <button
              data-testid="admin-logout-btn"
              onClick={logout}
              className="inline-flex items-center gap-2 border border-[#2a2a2a] hover:border-[#eab308] hover:text-[#eab308] px-3 md:px-4 py-2 font-condensed uppercase tracking-[0.2em] text-xs"
            >
              <LogOut size={14} /> <span className="hidden sm:inline">Logout</span>
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 md:px-10 py-10">
        {/* Page header */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
          <div>
            <div className="font-condensed uppercase tracking-[0.25em] text-xs text-[#eab308] mb-2">
              Applications
            </div>
            <h1 className="font-display text-4xl md:text-5xl leading-none">
              Pipeline.
            </h1>
          </div>
          <button
            data-testid="admin-refresh-btn"
            onClick={load}
            disabled={refreshing}
            className="self-start md:self-auto inline-flex items-center gap-2 text-sm font-condensed uppercase tracking-[0.2em] text-neutral-300 hover:text-[#eab308]"
          >
            <RefreshCw size={14} className={refreshing ? "animate-spin" : ""} /> Refresh
          </button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-[#2a2a2a] border border-[#2a2a2a] mb-8">
          <Stat label="Total" value={stats.total} testid="stat-total" />
          <Stat label="New" value={stats.new} accent testid="stat-new" />
          <Stat label="Contacted" value={stats.contacted} testid="stat-contacted" />
          <Stat label="Archived" value={stats.archived} testid="stat-archived" />
        </div>

        {/* Filters */}
        <form
          onSubmit={onApplyFilters}
          className="grid grid-cols-1 md:grid-cols-12 gap-3 mb-6"
        >
          <div className="md:col-span-5 relative">
            <Search
              size={16}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500"
            />
            <input
              data-testid="filter-search"
              value={filters.q}
              onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))}
              placeholder="Search name, email, phone…"
              className="w-full bg-[#141414] border border-[#2a2a2a] focus:border-[#eab308] outline-none pl-10 pr-4 py-2.5 text-sm rounded-sm"
            />
          </div>
          <select
            data-testid="filter-training-type"
            value={filters.training_type}
            onChange={(e) =>
              setFilters((f) => ({ ...f, training_type: e.target.value }))
            }
            className="md:col-span-2 bg-[#141414] border border-[#2a2a2a] focus:border-[#eab308] outline-none px-3 py-2.5 text-sm rounded-sm"
          >
            <option value="">All formats</option>
            <option value="remote">Remote</option>
            <option value="in-person">In-Person</option>
            <option value="hybrid">Hybrid</option>
          </select>
          <select
            data-testid="filter-level"
            value={filters.level}
            onChange={(e) => setFilters((f) => ({ ...f, level: e.target.value }))}
            className="md:col-span-2 bg-[#141414] border border-[#2a2a2a] focus:border-[#eab308] outline-none px-3 py-2.5 text-sm rounded-sm"
          >
            <option value="">All levels</option>
            <option value="middle-school">Middle School</option>
            <option value="high-school">High School</option>
            <option value="college">College</option>
            <option value="other">Other</option>
          </select>
          <select
            data-testid="filter-status"
            value={filters.status}
            onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))}
            className="md:col-span-2 bg-[#141414] border border-[#2a2a2a] focus:border-[#eab308] outline-none px-3 py-2.5 text-sm rounded-sm"
          >
            <option value="">All statuses</option>
            <option value="new">New</option>
            <option value="contacted">Contacted</option>
            <option value="archived">Archived</option>
          </select>
          <button
            data-testid="filter-apply-btn"
            type="submit"
            className="md:col-span-1 bg-[#eab308] hover:bg-[#facc15] text-black font-bold uppercase font-condensed tracking-widest text-xs py-2.5 rounded-sm"
          >
            Apply
          </button>
        </form>

        {/* Table */}
        <div className="border border-[#2a2a2a] bg-[#141414] overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="applications-table">
              <thead className="bg-[#0a0a0a] border-b border-[#2a2a2a]">
                <tr className="text-left text-neutral-400 font-condensed uppercase tracking-[0.2em] text-xs">
                  <th className="px-4 py-3">Date</th>
                  <th className="px-4 py-3">Name</th>
                  <th className="px-4 py-3 hidden md:table-cell">Contact</th>
                  <th className="px-4 py-3">Format</th>
                  <th className="px-4 py-3 hidden lg:table-cell">Level</th>
                  <th className="px-4 py-3 hidden lg:table-cell">Velo</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={8} className="px-4 py-12 text-center text-neutral-500">
                      <Loader2 className="inline animate-spin" size={18} /> Loading…
                    </td>
                  </tr>
                ) : filtered.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="px-4 py-16 text-center text-neutral-500">
                      <Inbox className="inline mb-2" size={28} />
                      <div className="font-condensed uppercase tracking-[0.2em] text-xs">
                        No applications match these filters
                      </div>
                    </td>
                  </tr>
                ) : (
                  filtered.map((a) => (
                    <tr
                      key={a.id}
                      data-testid={`application-row-${a.id}`}
                      className="border-b border-[#1f1f1f] hover:bg-[#0f0f0f] cursor-pointer"
                      onClick={() => setSelected(a)}
                    >
                      <td className="px-4 py-3 text-neutral-400 whitespace-nowrap">
                        {formatDate(a.submitted_at)}
                      </td>
                      <td className="px-4 py-3 font-semibold">{a.full_name}</td>
                      <td className="px-4 py-3 hidden md:table-cell text-neutral-300">
                        <div className="flex items-center gap-1">
                          <Mail size={12} className="text-neutral-500" /> {a.email}
                        </div>
                      </td>
                      <td className="px-4 py-3 capitalize">{a.training_type}</td>
                      <td className="px-4 py-3 hidden lg:table-cell capitalize">
                        {a.level?.replace("-", " ")}
                      </td>
                      <td className="px-4 py-3 hidden lg:table-cell text-neutral-400">
                        {a.current_velocity ? `${a.current_velocity} mph` : "—"}
                      </td>
                      <td className="px-4 py-3">
                        <StatusPill status={a.status || "new"} />
                      </td>
                      <td
                        className="px-4 py-3 text-right whitespace-nowrap"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <div className="inline-flex gap-1">
                          {a.status !== "contacted" && (
                            <IconBtn
                              testid={`row-mark-contacted-${a.id}`}
                              onClick={() => updateStatus(a.id, "contacted")}
                              icon={Check}
                              label="Mark contacted"
                            />
                          )}
                          {a.status !== "archived" && (
                            <IconBtn
                              testid={`row-archive-${a.id}`}
                              onClick={() => updateStatus(a.id, "archived")}
                              icon={Archive}
                              label="Archive"
                            />
                          )}
                          {a.status !== "new" && (
                            <IconBtn
                              testid={`row-mark-new-${a.id}`}
                              onClick={() => updateStatus(a.id, "new")}
                              icon={Inbox}
                              label="Mark new"
                            />
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {selected && (
        <DetailDrawer
          app={selected}
          onClose={() => setSelected(null)}
          onUpdate={(s) => updateStatus(selected.id, s)}
        />
      )}
    </main>
  );
}

function Stat({ label, value, accent, testid }) {
  return (
    <div
      data-testid={testid}
      className={`bg-[#0a0a0a] px-5 py-5 ${accent ? "" : ""}`}
    >
      <div
        className={`font-display text-3xl md:text-4xl ${
          accent ? "text-[#eab308]" : "text-white"
        }`}
      >
        {value}
      </div>
      <div className="font-condensed uppercase text-[11px] tracking-[0.2em] text-neutral-400 mt-1">
        {label}
      </div>
    </div>
  );
}

function StatusPill({ status }) {
  return (
    <span
      data-testid={`status-pill-${status}`}
      className={`inline-block px-2.5 py-1 font-condensed uppercase tracking-[0.18em] text-[10px] font-bold ${
        STATUS_STYLES[status] || STATUS_STYLES.new
      }`}
    >
      {status}
    </span>
  );
}

function IconBtn({ icon: Icon, onClick, label, testid }) {
  return (
    <button
      title={label}
      aria-label={label}
      data-testid={testid}
      onClick={onClick}
      className="w-8 h-8 inline-flex items-center justify-center border border-[#2a2a2a] hover:border-[#eab308] hover:text-[#eab308] transition-colors"
    >
      <Icon size={14} />
    </button>
  );
}

function DetailDrawer({ app, onClose, onUpdate }) {
  return (
    <div
      data-testid="detail-drawer"
      className="fixed inset-0 z-50 flex"
      onClick={onClose}
    >
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
      <div
        className="relative ml-auto h-full w-full md:w-[520px] bg-[#0a0a0a] border-l border-[#2a2a2a] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 bg-[#0a0a0a] border-b border-[#2a2a2a] p-5 flex items-center justify-between">
          <div>
            <div className="font-condensed uppercase tracking-[0.25em] text-xs text-[#eab308]">
              Applicant
            </div>
            <div className="font-display text-2xl">{app.full_name}</div>
          </div>
          <button
            data-testid="drawer-close"
            onClick={onClose}
            className="w-9 h-9 border border-[#2a2a2a] hover:border-[#eab308] hover:text-[#eab308] flex items-center justify-center"
          >
            <X size={16} />
          </button>
        </div>

        <div className="p-5 space-y-6">
          <div className="flex flex-wrap gap-2">
            <StatusPill status={app.status || "new"} />
            <span className="px-2.5 py-1 font-condensed uppercase tracking-[0.18em] text-[10px] border border-[#2a2a2a] text-neutral-300">
              {app.training_type}
            </span>
            <span className="px-2.5 py-1 font-condensed uppercase tracking-[0.18em] text-[10px] border border-[#2a2a2a] text-neutral-300">
              {(app.level || "").replace("-", " ")}
            </span>
          </div>

          <div className="grid grid-cols-1 gap-3">
            <RowItem icon={Mail} label="Email" value={app.email} link={`mailto:${app.email}`} />
            <RowItem icon={Phone} label="Phone" value={app.phone} link={`tel:${app.phone}`} />
            <RowItem
              icon={Calendar}
              label="Preferred"
              value={`${app.preferred_date || "—"} • ${app.preferred_time || "—"}`}
            />
          </div>

          <Section title="Player Profile">
            <KV k="Age" v={app.age} />
            <KV k="Position" v={app.position} />
            <KV k="Current Velo" v={app.current_velocity ? `${app.current_velocity} mph` : "—"} />
            <KV k="Target Velo" v={app.target_velocity ? `${app.target_velocity} mph` : "—"} />
            <KV k="Years Pitching" v={app.years_pitching || "—"} />
          </Section>

          {app.goals && (
            <Section title="Goals & Background">
              <p className="text-neutral-300 leading-relaxed text-sm whitespace-pre-line">
                {app.goals}
              </p>
            </Section>
          )}

          <Section title="Submission">
            <KV k="ID" v={app.id} />
            <KV k="Submitted" v={formatDate(app.submitted_at, true)} />
          </Section>

          <div className="border-t border-[#2a2a2a] pt-4">
            <div className="font-condensed uppercase tracking-[0.25em] text-xs text-neutral-400 mb-3">
              Update Status
            </div>
            <div className="flex flex-wrap gap-2">
              {["new", "contacted", "archived"].map((s) => (
                <button
                  key={s}
                  data-testid={`drawer-set-${s}`}
                  onClick={() => onUpdate(s)}
                  className={`px-3 py-2 font-condensed uppercase tracking-[0.2em] text-xs border ${
                    app.status === s
                      ? "border-[#eab308] text-[#eab308]"
                      : "border-[#2a2a2a] hover:border-[#eab308]"
                  }`}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function RowItem({ icon: Icon, label, value, link }) {
  const inner = (
    <div className="flex items-center gap-3 p-3 border border-[#2a2a2a] bg-[#141414]">
      <Icon size={16} className="text-[#eab308]" />
      <div>
        <div className="font-condensed uppercase tracking-[0.2em] text-[10px] text-neutral-500">
          {label}
        </div>
        <div className="text-sm text-white">{value}</div>
      </div>
    </div>
  );
  return link ? (
    <a href={link} className="block hover:border-[#eab308]">
      {inner}
    </a>
  ) : (
    inner
  );
}

function Section({ title, children }) {
  return (
    <div>
      <div className="font-condensed uppercase tracking-[0.25em] text-xs text-neutral-400 mb-3">
        {title}
      </div>
      <div className="space-y-2">{children}</div>
    </div>
  );
}

function KV({ k, v }) {
  return (
    <div className="flex justify-between gap-3 text-sm border-b border-[#1a1a1a] pb-2">
      <span className="text-neutral-500">{k}</span>
      <span className="text-neutral-200 text-right truncate max-w-[60%]">{v}</span>
    </div>
  );
}

function formatDate(d, withTime = false) {
  if (!d) return "—";
  try {
    const date = new Date(d);
    const opts = withTime
      ? { month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit" }
      : { month: "short", day: "numeric", year: "numeric" };
    return date.toLocaleString(undefined, opts);
  } catch {
    return d;
  }
}
